import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import IdBadge from "components/IdBadge";
import { useMemo, useState } from "react";
import AxisDetailsDrawer from "./AxisDetailsDrawer";
import CreateAxisDialog from "./CreateAxisDialog";
import { regionKey, useLidarClearance } from "./LidarClearanceContext";
import { buildGradient } from "./colorScaleDisplay";
import t from "./translations";

const COLUMN_COUNT = 4;

export default function AxesTab() {
  const { axes, colorScaleByKey, loading } = useLidarClearance();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const selectedAxis = useMemo(
    () =>
      axes.find((axis) => regionKey(axis.region, axis.id) === selectedKey) ??
      null,
    [axes, selectedKey]
  );

  const rows = useMemo(
    () =>
      axes.map((axis) => {
        const scale = colorScaleByKey.get(
          regionKey(axis.region, axis.color_scale)
        );
        return {
          axis,
          gradient: scale
            ? buildGradient(scale.colors, axis.min_value, axis.max_value)
            : undefined,
        };
      }),
    [axes, colorScaleByKey]
  );

  return (
    <Box data-testid="axes-tab">
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          data-testid="axes-tab.new-axis"
        >
          {t.NewAxisButton}
        </Button>
      </Box>
      <TableContainer component={Paper} data-testid="axes-table">
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: 90 }}>
                {t.IdColumn}
              </TableCell>
              <TableCell>{t.NameColumn}</TableCell>
              <TableCell sx={{ width: 100 }}>{t.RangeColumn}</TableCell>
              <TableCell sx={{ width: 160 }}>{t.ColorScaleColumn}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {axes.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {rows.map(({ axis, gradient }) => {
              const key = regionKey(axis.region, axis.id);

              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => setSelectedKey(key)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`axes-tab.row-${axis.region}-${axis.id}`}
                >
                  <TableCell align="center">
                    <IdBadge prefix={axis.region.toUpperCase()} id={axis.id} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {axis.name_en}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {axis.min_value}–{axis.max_value}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        height: 8,
                        backgroundColor: "grey.300",
                        backgroundImage: gradient,
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <AxisDetailsDrawer
        axis={selectedAxis}
        onClose={() => setSelectedKey(null)}
      />
      <CreateAxisDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
